import { BrutusCaseRunCreateSampleModel, BrutusRelationModel, BrutusTestType, SampleSex } from '@app/api2/api';
import { customAlphabet } from 'nanoid';
import * as React from 'react';
import { Button, Checkbox, CheckboxProps, Dropdown, Form, Grid, GridColumn, GridRow, Icon, Label, Modal, Table, TableBody, TableCell, TableHeader, TableHeaderCell, TableRow } from 'semantic-ui-react';
import { PedigreeGraph } from './Pedigree/Graph';
import { GenderSelectorSelect } from './GenderSelector';
import { BPButton } from '@/components/bp/bpbutton';
import { BPCheckbox } from '@/components/bp/bpcheckbox';


interface IPedigreeEditorProps {
    testType: BrutusTestType;
    readonly?: boolean;
    references: BrutusCaseRunCreateSampleModel[];
    relations: BrutusRelationModel[];
    onRemoveRelation?: (relation: BrutusRelationModel) => void;
    onReferenceSelected?: (reference: BrutusCaseRunCreateSampleModel, selected: boolean) => void;
    onReferenceSexChange?: (reference: BrutusCaseRunCreateSampleModel, isMale: boolean) => void;
    onMissingPersonChange?: (reference: BrutusCaseRunCreateSampleModel, isMissing: boolean) => void;
    onRelationAdded?: (relation: BrutusRelationModel) => void;
    onUntestedAdded?: (reference: BrutusCaseRunCreateSampleModel) => void;
    onRelationUpdate?: (oldrel: BrutusRelationModel, newrel: BrutusRelationModel) => void;
}

export const PedigreeEditor: React.FunctionComponent<IPedigreeEditorProps> = (props) => {
    const [editedRelation, setEditedRelation] = React.useState<BrutusRelationModel>()
    const [newRelationForAncestorId, setNewRelationForAncestorId] = React.useState<number>()

    const selectedReferences = props.references.filter(r => r.selected)

    const pedigreeRelations = props.relations?.map(x => { return { x: x, relationId: `${x.ancestorId}${x.descendantId}`, ancestor: x.ancestorId.toString(), descendant: x.descendantId.toString(), questioned: x.questioned } }) ?? [];
    return <>
        <div className="field">
            <label><span style={{ marginRight: 10 }}>{window.translation.common.referencePlural}</span>
                {!props.readonly &&
                    <NewUntested onOk={(values) => {
                        if (props.onUntestedAdded)
                            props.onUntestedAdded(values)
                    }} goBack={() => { }} />
                }
            </label>
            <Table compact basic verticalAlign='middle' selectable>
                <TableHeader>
                    <TableRow>
                        {!props.readonly &&
                            <TableHeaderCell>Use</TableHeaderCell>
                        }
                        <TableHeaderCell width={1}>{window.translation.brutus.sex}</TableHeaderCell>
                        <TableHeaderCell>{window.translation.common.reference}</TableHeaderCell>
                        <TableHeaderCell>{window.translation.brutus.children}</TableHeaderCell>
                        {props.testType == BrutusTestType.UHR &&
                            <TableHeaderCell>{'Missing person'}</TableHeaderCell>
                        }
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {props.references?.map((curRef, idx) => (
                        <TableRow key={`ref_${curRef.id}_${curRef.name}`}>
                            {!props.readonly &&
                                <TableCell width={1}>
                                    <BPCheckbox type={'checkbox'} checked={curRef.selected} onChange={(ev, d) => {
                                        if (props.onReferenceSelected)
                                            props.onReferenceSelected(curRef, d.checked)
                                    }}></BPCheckbox>
                                </TableCell>
                            }
                            <TableCell width={1}>
                                <GenderSelectorSelect name='gender' readOnly={props.readonly} showIcon showLabel={false}
                                    value={curRef.isMale ? SampleSex.Male : SampleSex.Female}
                                    onChange={(ev, value) => {
                                        if (props.onReferenceSexChange)
                                            props.onReferenceSexChange(curRef, value == SampleSex.Male)
                                    }} />
                            </TableCell>
                            <TableCell>
                                <label htmlFor={`refs_${idx}_selected`}>
                                    {curRef.name}
                                </label>
                            </TableCell>
                            <TableCell>
                                {props.relations?.filter(x => x.ancestorId == curRef.id).map(rel => {
                                    return props.readonly ? (
                                        <div key={`${rel.ancestorId}_${rel.descendantId}`}>
                                            {props.references.find(ref => ref.id == rel.descendantId)?.name} {rel.questioned ? '(?)' : ""}
                                        </div>
                                    ) : (<Label key={`${rel.ancestorId}_${rel.descendantId}`} basic>
                                        {`${curRef.name} is Parent of ${props.references.find(ref => ref.id == rel.descendantId)?.name} ${rel.questioned ? '(?)' : ""}`}
                                        {!props.readonly && <>
                                            <a onClick={() => {
                                                if (props.onRemoveRelation)
                                                    props.onRemoveRelation(rel)
                                            }}>
                                                <Icon name={'delete'}></Icon>
                                            </a>
                                            <a onClick={() => {
                                                setEditedRelation(rel)
                                            }}>
                                                <Icon name={'edit'}></Icon>
                                            </a>
                                        </>
                                        }
                                    </Label>)
                                })}
                                {
                                    curRef.selected && !props.readonly &&
                                    <BPButton
                                        content={'Add'}
                                        size='tiny'
                                        icon={'add'}
                                        compact
                                        circular
                                        floated='right'
                                        type='button'
                                        onClick={() => {
                                            setNewRelationForAncestorId(curRef.id)
                                        }}
                                    />
                                }
                            </TableCell>
                            {props.testType == BrutusTestType.UHR &&
                                <TableCell width={2} textAlign="center">
                                    <BPCheckbox type={'checkbox'} checked={curRef.isMissingPerson} onChange={(ev, d) => {
                                        if (props.onMissingPersonChange)
                                            props.onMissingPersonChange(curRef, d.checked)
                                    }}></BPCheckbox>
                                </TableCell>
                            }
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            <PedigreeGraph
                readOnly={props.readonly}
                people={selectedReferences?.map(x => { return { displayName: x.name, imported: !x.isUntested, missing: x.isMissingPerson, personId: x.id.toString(), sampleName: x.name, sex: x.isMale ? "M" : "F", systems: [] } }) ?? []}
                relations={pedigreeRelations}
                onRelationAdd={(parent, child) => {
                    if (props.onRelationAdded) {
                        props.onRelationAdded({
                            ancestorId: parseInt(parent.personId),
                            descendantId: parseInt(child.personId), questioned: false
                        })
                    }
                }}
                onRelationRemove={id => {
                    if (props.onRemoveRelation) {
                        let rel = pedigreeRelations.find(x => x.relationId == id)
                        props.onRemoveRelation({
                            ancestorId: parseInt(rel.ancestor),
                            descendantId: parseInt(rel.descendant), questioned: false
                        })
                    }
                }}
                onRelationEdit={id => {
                    let rel = pedigreeRelations.find(x => x.relationId == id)

                    setEditedRelation(rel.x)
                }}

            />
            <NewRelation testType={props.testType} ancestorId={newRelationForAncestorId} selectedRelation={editedRelation} references={selectedReferences}
                relations={props.relations}
                onOk={(values) => {
                    if (editedRelation) {
                        if (props.onRelationUpdate) {
                            props.onRelationUpdate(editedRelation, values)
                            setEditedRelation(null)
                        }
                    } else {
                        if (props.onRelationAdded) {
                            props.onRelationAdded(values)
                            setNewRelationForAncestorId(null)
                        }
                    }
                    setNewRelationForAncestorId(null)
                }} onCancel={() => {
                    setEditedRelation(null)
                    setNewRelationForAncestorId(null)
                }} />
        </div>
    </>;
};

interface INewUntestedProps {
    goBack: () => void;
    onOk: (values: BrutusCaseRunCreateSampleModel) => void;
}

export const NewUntested: React.FunctionComponent<INewUntestedProps> = (props) => {
    const t = window.translation;

    const [name, setName] = React.useState<string>()
    const [gender, setGender] = React.useState<SampleSex>(SampleSex.Male)
    const [open, setOpen] = React.useState(false)

    const nanoid1 = customAlphabet('1234567890', 5)

    return <>
        <Modal as={Form}
            onClose={() => { setOpen(false); props.goBack() }}
            onOpen={() => setOpen(true)}
            open={open}
            trigger={<BPButton
                content={'Add untested person'}
                size='small'
                icon={'add'}
                compact
                type='button'
            />}>
            <Modal.Header>{'Add untested person'}</Modal.Header>
            <Modal.Content>
                <Grid>
                    <GridRow>
                        <GridColumn width={3} verticalAlign={'top'}>
                            <Form.Field label={t.brutus.sex} control={GenderSelectorSelect} showIcon={true} value={gender} name={'gender'} onChange={(ev, sex) => setGender(sex)}></Form.Field>
                        </GridColumn>
                        <GridColumn width={13} verticalAlign={'middle'}>
                            <Form.Input label={t.common.name} value={name} onChange={(ev, data) => setName(data.value)}></Form.Input>
                        </GridColumn>
                    </GridRow>
                </Grid>
            </Modal.Content>
            <Modal.Actions>
                <BPButton type='button' onClick={() => {
                    props.onOk({ id: -parseInt(nanoid1()), isMale: gender == SampleSex.Male, isMissingPerson: false, isUntested: true, selected: true, name: name } as BrutusCaseRunCreateSampleModel)
                    setName('')
                    setOpen(false)
                }}>Add</BPButton>

                <BPButton onClick={() => setOpen(false)}>
                    {'Cancel'}
                </BPButton>
            </Modal.Actions>
        </Modal>
    </>;
};


interface INewRelationProps {
    ancestorId?: number; // if given, then in new mode
    selectedRelation?: BrutusRelationModel; // if given, then in edit mode
    onOk: (values: BrutusRelationModel) => void;
    onCancel?: () => void;
    references: Array<BrutusCaseRunCreateSampleModel>;
    relations: Array<BrutusRelationModel>;
    testType: BrutusTestType;
}

export const NewRelation: React.FunctionComponent<INewRelationProps> = (props) => {
    const t = window.translation;

    const ancestorId = props.ancestorId ?? props.selectedRelation?.ancestorId;
    const ancestorsRelations = props.relations?.filter(x => x.ancestorId == ancestorId) ?? []
    const otherRelations = props.relations?.filter(x => x.descendantId == ancestorId) ?? []

    const availableReferences = props.references?.filter(x => x.id != ancestorId)
        .filter(reference => otherRelations.findIndex(relation => relation.ancestorId == reference.id) == -1)
        // .filter(reference => ancestorsRelations.findIndex(relation => !(relation.ancestorId == props.selectedRelation?.ancestorId && relation.descendantId == props.selectedRelation?.descendantId) || relation.descendantId == reference.id) == -1)
        .map(m => { return { key: m.id, value: m.id, text: m.name }; }) ?? []

    const [referenceId, setReferenceId] = React.useState<number>(props.selectedRelation?.descendantId ?? availableReferences.find(x => true)?.value as number)
    const [questioned, setQuestioned] = React.useState<boolean>(false)
    const [open, setOpen] = React.useState<boolean>(false)

    React.useEffect(() => {
        setReferenceId(props.selectedRelation?.descendantId ?? availableReferences.find(x => true)?.value as number)
        setOpen(props.selectedRelation != null || props.ancestorId != null)
    }, [props.selectedRelation, props.ancestorId, open])

    const ancestorName = props.references.find(r => r.id == ancestorId)?.name;
    return <Modal open={open}>
        <Modal.Header>{ancestorName}</Modal.Header>
        <Modal.Content>
            <Table basic singleLine>
                <TableBody>
                    <TableCell width={1}>Is Parent to</TableCell>
                    <TableCell width={5}>
                        <Dropdown selection options={availableReferences} value={referenceId} onChange={(ev, data) => {
                            setReferenceId(data.value as number)
                        }} />
                    </TableCell>
                    <TableCell>
                        {props.testType == BrutusTestType.Kinship &&
                            <BPCheckbox label="Question This Relationship" checked={questioned} onChange={(e, data: CheckboxProps) => {
                                setQuestioned(data.checked)
                            }}></BPCheckbox>
                        }
                    </TableCell>
                </TableBody>
            </Table>
        </Modal.Content>
        <Modal.Actions>
            <BPButton type='button' onClick={() => {
                props.onOk({ ancestorId: ancestorId, descendantId: referenceId, questioned: questioned, } as BrutusRelationModel)
            }}>Save</BPButton>

            <BPButton onClick={() => {
                setOpen(false)
                if (props.onCancel)
                    props.onCancel();
            }}>
                {'Cancel'}
            </BPButton>
        </Modal.Actions>
    </Modal>;
};

